import { useEffect, useState } from 'react';

import { loadTemplateSession } from './planTemplateSource';
import { useLocale } from '../primitives/useLocale';
import type { ProgramApiFetch } from '../ProgramEditor/types';

// Render-time resolver for the PER-EXERCISE + PER-SET coach cues (the 💬 entry
// instruction and the 🎯 SetPrescription note), keyed by exercise slug.
//
// This is the SINGLE display source for plan cues (note-model consolidation,
// 2026-07-03): the draft never carries them. The cues live on the program/plan
// template only, are display-only, and are never snapshotted onto the saved
// session (feedback_no_denormalized_display_snapshots). Resolving here (not
// from the draft) makes them correct by construction across fresh-start / EDIT
// / REPEAT AND across an in-workout exercise swap — the cue follows the slug,
// so a swapped-in exercise shows ITS cue (or none), never the replaced one's.
//
// WHERE the template comes from is planTemplateSource's job (cache-first, then
// the server — LG-30). This hook only turns it into the slug→cues map; it
// never reads storage or the network itself, and never touches the draft /
// save path.

export type PlanEntryCues = {
  /** Per-exercise instruction (slot instructions → entry note, 💬). */
  instruction?: string;
  /** Per-set cues (🎯), index-aligned with the planned sets. */
  perSetNotes: string[];
};

// The minimal shape both sources expose: a program-resolved session entry and a
// single-day Plan entry are field-compatible for cue extraction.
type CueSourceEntry = {
  exerciseId: string;
  notes?: string;
  sets: ReadonlyArray<{ notes?: string }>;
};

// Build the slug→cues map from either source's entries. First entry per slug
// wins (planned order matches logged order in the common case; a repeated
// exercise keeps the first).
const cuesFromEntries = (
  entries: ReadonlyArray<CueSourceEntry>,
): Map<string, PlanEntryCues> => {
  const map = new Map<string, PlanEntryCues>();
  for (const e of entries) {
    if (map.has(e.exerciseId)) continue;
    map.set(e.exerciseId, {
      ...(e.notes ? { instruction: e.notes } : {}),
      perSetNotes: e.sets.map((s) => s.notes ?? ''),
    });
  }
  return map;
};

export function usePlanCues(
  fromPlanId: string | undefined,
  apiFetch: ProgramApiFetch,
): Map<string, PlanEntryCues> {
  const lang = useLocale();
  const [cues, setCues] = useState<Map<string, PlanEntryCues>>(
    () => new Map(),
  );

  useEffect(() => {
    if (!fromPlanId) {
      setCues(new Map());
      return;
    }
    let alive = true;
    void (async () => {
      const session = await loadTemplateSession(fromPlanId, apiFetch, lang);
      if (!alive || !session) return;
      setCues(cuesFromEntries(session.entries));
    })();
    return () => {
      alive = false;
    };
  }, [fromPlanId, apiFetch, lang]);

  return cues;
}
